<div class="max-w-3xl mx-auto p-6 bg-white rounded-xl shadow-md space-y-8">
  <section>
    <h2 class="text-2xl font-semibold text-gray-800 border-b pb-2 mb-4">General Rules</h2>
    <div class="space-y-4">
      <cac-field [control]="generalForm.controls.minimumCheckoutPrice" label="Minimum checkout price"
        controlType="input" inputType="number" placeholder="Enter minimum checkout price">
      </cac-field>
    </div>
  </section>

  <section>
    <h2 class="text-2xl font-semibold text-gray-800 border-b pb-2 mb-4">Free Shipping Rules</h2>
    <div class="space-y-4">
      <cac-field [control]="freeShippingForm.controls.isFreeShippingEnabled" label="Free shipping" controlType="select"
        [items]="[
          { value: true, label: 'Enabled' },
          { value: false, label: 'Disabled' }
        ]" placeholder="Select free shipping status">
      </cac-field>

      @if (freeShippingForm.controls.isFreeShippingEnabled.value) {
      <cac-field [control]="freeShippingForm.controls.minimumCartPriceForFreeShipping"
        label="Minimum cart price for free shipping" controlType="input" inputType="number"
        placeholder="Enter minimum cart price for free shipping">
      </cac-field>
      }
    </div>
  </section>

  <section>
    <h2 class="text-xl font-semibold mb-4">Customer / Product Free Delivery</h2>
    <cac-field [control]="targetedShippingForm.controls.isTargetedFreeShippingEnabled" label="Targeted free delivery"
      controlType="select" [items]="[{ value: true, label: 'Enabled' }, { value: false, label: 'Disabled' }]" />
    @if (targetedShippingForm.controls.isTargetedFreeShippingEnabled.value) {
      <div class="mt-4 space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2">Customers (optional)</label>
          <app-lazy-dropdown [apiAdapter]="customerApiAdapter" [itemMapper]="customerItemMapper"
            [clearOnSelection]="true" [excludeIds]="selectedCustomerIds" searchField="SearchQuery"
            placeholder="Search customers" (selectionChange)="addShippingSelection('customers', $event)" />
          @for (item of freeShippingCustomers; track item.id) {
            <div class="flex items-center justify-between gap-3 border-b py-2">
              <span class="text-sm break-words">{{ item.label }}</span>
              <button type="button" (click)="removeShippingSelection('customers', item.id)" title="Remove customer" class="shrink-0 p-2">
                <cac-icon icon="trash" size="1rem" />
              </button>
            </div>
          } @empty { <p class="text-sm mt-2">All customers</p> }
        </div>
        <div>
          <label class="block text-sm font-medium mb-2">Products (optional)</label>
          <app-lazy-dropdown [apiAdapter]="productApiAdapter" [itemMapper]="productItemMapper"
            [clearOnSelection]="true" [excludeIds]="selectedProductIds" searchField="SearchQuery" [showCustomTemplate]="true"
            placeholder="Search products" (selectionChange)="addShippingSelection('products', $event)" />
          @for (item of freeShippingProducts; track item.id) {
            <div class="flex items-center justify-between gap-3 border-b py-2">
              <div class="flex items-center gap-3 min-w-0">
                <div class="w-10 h-10 shrink-0 bg-gray-100 overflow-hidden rounded">
                  @if (item['image']?.url) {
                    <img [src]="item['image'].url" [alt]="item.label" class="w-full h-full object-cover" loading="lazy" />
                  }
                </div>
                <span class="text-sm break-words">{{ item.label }}</span>
              </div>
              <button type="button" (click)="removeShippingSelection('products', item.id)" title="Remove product" class="shrink-0 p-2">
                <cac-icon icon="trash" size="1rem" />
              </button>
            </div>
          } @empty { <p class="text-sm mt-2">All products</p> }
        </div>
      </div>
    }
  </section>

  <section>
    <h2 class="text-2xl font-semibold text-gray-800 border-b pb-2 mb-4">Gift Rules</h2>
    <div class="space-y-6">
      <cac-field [control]="giftForm.controls.isGiftAllowed" label="Gift for purchase" controlType="select" [items]="[
          { value: true, label: 'Enabled' },
          { value: false, label: 'Disabled' }
        ]" placeholder="Select gift status">
      </cac-field>

      @if (giftForm.controls.isGiftAllowed.value) {
      <div class="space-y-6">
        <!-- Gift Tiers -->
        @for (tier of giftTiers$.value; track $index; let i = $index) {
        <div class="border-2 rounded-3xl p-6 bg-gradient-to-br from-blue-50/50 to-purple-50/50 space-y-4">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-xl font-semibold text-gray-900">
              Tier {{ i + 1 }}
            </h3>
            @if (giftTiers$.value.length > 1) {
            <button (click)="removeGiftTier(i)"
              class="text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2"
              title="Remove Tier">
              <cac-icon icon="trash" size="1rem"></cac-icon>
              <span class="text-sm font-medium">Remove Tier</span>
            </button>
            }
          </div>

          <!-- Minimum Cart Price -->
          <div>
            <cac-field [control]="tier.minimumCartPrice"
              [label]="i === 0 ? 'Minimum cart price' : 'Minimum cart price (must be > ' + (giftTiers$.value[i - 1].minimumCartPrice.value || 0) + ')'"
              controlType="input" inputType="number" placeholder="Enter minimum cart price">
            </cac-field>
            @if (validateTierMinimumPrice(i); as error) {
            <p class="text-red-600 text-sm mt-1">{{ error }}</p>
            }
          </div>

          <!-- Gift Selection -->
          <div class="border rounded-2xl p-4 bg-white/60">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-md font-semibold text-gray-900">
                Add Gifts <span class="text-red-500">*</span>
              </h4>
            </div>

            <div class="mb-3">
              <app-lazy-dropdown #giftDropdown [multiple]="false" [searchable]="true" [clearable]="true"
                [clearOnSelection]="true" [placeholder]="'Search and select gifts...'"
                [hint]="'Type to search for products. Selected items will be added to the list below.'" [pageSize]="20"
                [apiAdapter]="productApiAdapter" [itemMapper]="productItemMapper"
                [excludeIds]="getSelectedGiftIds(i)" idsFilterKey="ProductIds" searchField="EnglishName"
                [duplicateMessage]="'Gift is already selected'" (selectionChange)="onGiftChange($event, i)"
                class="w-full" />
            </div>
          </div>

          <!-- Selected Gifts for this tier -->
          <div class="border rounded-2xl p-4 bg-white/60">
            <h4 class="text-md font-semibold text-gray-900 mb-3">
              Selected Gifts
            </h4>

            @if (tier.selectedGifts.value.length > 0) {
            <div class="space-y-2">
              @for (gift of tier.selectedGifts.value; track gift.id) {
              <div
                class="group bg-white rounded-xl border border-gray-200 p-3 hover:shadow-sm transition-all duration-200">
                <div class="flex items-center gap-3">
                  <!-- Gift Image -->
                  <div class="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg overflow-hidden">
                    @if (gift.productFirstImage?.url) {
                    <img [src]="gift.productFirstImage?.url" [alt]="gift.name.english"
                      class="w-full h-full object-cover" loading="lazy" />
                    } @else {
                    <div class="w-full h-full bg-gray-200 flex items-center justify-center">
                      <span class="text-gray-400 text-xs">No Image</span>
                    </div>
                    }
                  </div>

                  <!-- Gift Info -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-start gap-2 flex-wrap">
                      <span class="text-sm font-medium text-gray-900 break-words min-w-0 flex-1">{{ gift.name.english }}</span>
                      <span class="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 inline-block">
                        ID: {{ gift.id }}
                      </span>
                    </div>
                    @if (gift.name.arabic && gift.name.arabic !== gift.name.english) {
                    <p class="text-xs text-gray-600 break-words mt-0.5">{{ gift.name.arabic }}</p>
                    }
                  </div>

                  <!-- Remove Action -->
                  <div
                    class="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button (click)="removeGift(i, gift.id)"
                      class="text-gray-500 hover:text-red-600 w-7 h-7 rounded-full transition-colors flex items-center justify-center"
                      title="Remove Gift">
                      <cac-icon icon="trash" size="1rem"></cac-icon>
                    </button>
                  </div>
                </div>
              </div>
              }
            </div>
            } @else {
            <div class="text-center py-8">
              <p class="text-gray-500 mb-2">No gifts selected yet</p>
              <p class="text-xs text-gray-400">Use the search dropdown above to find and select gifts</p>
              <p class="text-xs text-red-500 mt-1">At least one gift is required</p>
            </div>
            }
          </div>
        </div>
        }

        <!-- Add New Tier Button -->
        <div class="flex justify-center">
          <cac-button (click)="addGiftTier()" icon="plus" theme="primary">
            Add Another Tier
          </cac-button>
        </div>
      </div>
      }
    </div>
  </section>

  <div class="flex justify-end">
    <cac-button (onClick)="save($event)" [disabled]="!isFormValid()">Save</cac-button>
  </div>
</div>
